import * as OTPAuth from 'otpauth';

/**
 * Generate a new TOTP secret for a user
 */
export function generateTOTPSecret(email: string): { secret: string; uri: string } {
  const totp = new OTPAuth.TOTP({
    issuer: 'Dining Steward',
    label: email,
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
  });

  return {
    secret: totp.secret.base32,
    uri: totp.toString(),
  };
}

/**
 * Verify a TOTP code against a secret
 */
export function verifyTOTPCode(secret: string, token: string): boolean {
  const totp = new OTPAuth.TOTP({
    secret: OTPAuth.Secret.fromBase32(secret),
    algorithm: 'SHA1',
    digits: 6,
    period: 30,
  });

  // Allow 1 period of grace (30 seconds before/after)
  const delta = totp.validate({
    token,
    window: 1,
  });

  return delta !== null;
}

/**
 * Generate backup codes for 2FA recovery
 */
export function generateBackupCodes(count: number = 10): string[] {
  const codes: string[] = [];
  
  for (let i = 0; i < count; i++) {
    // Generate an 8-character alphanumeric code
    const code = Array.from({ length: 8 }, () => 
      '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 36)]
    ).join('');
    
    // Format as XXXX-XXXX for readability
    codes.push(`${code.slice(0, 4)}-${code.slice(4)}`);
  }
  
  return codes;
}

/**
 * Get device fingerprint information
 */
export function getDeviceInfo(): { name: string; userAgent: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';

  // Detect browser
  if (ua.includes('Firefox/')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg/')) {
    browser = 'Edge';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari';
  }

  // Detect OS
  if (ua.includes('Windows')) {
    os = 'Windows';
  } else if (ua.includes('Mac')) {
    os = 'macOS';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  } else if (ua.includes('Android')) {
    os = 'Android';
  } else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
  }

  return {
    name: `${browser} on ${os}`,
    userAgent: ua,
  };
}

/**
 * Generate a unique device ID based on browser fingerprint
 * This ID will be consistent for the same browser/device
 */
export function generateDeviceId(): string {
  // Check if we have a stored device ID in localStorage
  const stored = localStorage.getItem('deviceId');
  if (stored) {
    return stored;
  }

  // Generate new device ID based on user agent hash
  const info = getDeviceInfo();
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 15);
  
  // Create a hash of the user agent for consistency
  const hash = btoa(info.userAgent).substring(0, 20);
  const deviceId = `${hash}-${timestamp}-${random}`;
  
  // Store it for future use
  localStorage.setItem('deviceId', deviceId);
  
  return deviceId;
}

/**
 * Clear the stored device ID (useful for testing or logging out)
 */
export function clearDeviceId(): void {
  localStorage.removeItem('deviceId');
}

